/**
 * Shared dependencies
 */
import type { StoreFunctions } from '../../shared/types/store';
import type { List, ListRecordWithItems, ListItem, RecipeListItem } from '../../shared/types/lists';
import type { WP_Recipe } from '../../shared/types/recipe';

/**
 * Internal dependencies
 */
import { getUserLists, getUserListsWithItems, getRecipes } from '../requests';

/**
 * Converts a list array to a Record structure keyed by list ID.
 *
 * @param lists - Array of user lists.
 * @return Record of lists with empty items arrays.
 */
function initializeListsRecord(lists: List[]): ListRecordWithItems {
	return lists.reduce((acc, list) => {
		acc[list.id] = {
			...list,
			items: [],
			itemCount: 0,
		};
		return acc;
	}, {} as ListRecordWithItems);
}

/**
 * Transforms API item data into a RecipeListItem.
 *
 * @param apiItem - Raw API item data with listId.
 * @param recipe - Associated recipe data (if available).
 * @return Transformed RecipeListItem.
 */
function transformToListItem(
	apiItem: ListItem & { listId: string },
	recipe: WP_Recipe | undefined
): RecipeListItem {
	return {
		completed: apiItem.completed,
		completedBy: apiItem.completedBy,
		matterName: apiItem.matterName,
		matterId: apiItem.matterId,
		matterQuantity: apiItem.matterQuantity,
		matterType: apiItem.matterType,
		note: apiItem.note,
		parentRecipeIds: apiItem.parentRecipeIds || [],
		userMatterCategory: apiItem.userMatterCategory,
		userMatterDayOfTheWeek: apiItem.userMatterDayOfTheWeek,
		userMatterId: apiItem.userMatterId,
		userMatterName: apiItem.userMatterName,
		userMatterQuantity: apiItem.userMatterQuantity,
		userMatterType: apiItem.userMatterType,
		order: apiItem.order,
		createDate: apiItem.createDate,
		createdBy: apiItem.createdBy,
		modificationDate: apiItem.modificationDate,
		modifiedBy: apiItem.modifiedBy,
		name: recipe?.name || apiItem?.matterName || undefined,
		...recipe, // Add and replace with recipe data if available
		/**
		 * If recipe is undefined, we want to mark our list item accordingly.
		 * This is the case when recipe has been removed/deleted but still exists in user's list.
		 */
		isPublished: Boolean(recipe),
		/**
		 * Make sure we save Firebase ID. We need this for operations like delete.
		 * MatterId is used to compare recipes based on JamixId or similar.
		 */
		id: apiItem.id,
	} as RecipeListItem;
}

/**
 * Fetches all user lists and updates the store.
 * Also dispatches a ListEvent to notify other parts of the app.
 *
 * @param fn - Function to update the store state.
 * @return Promise that resolves to an array of user lists.
 */
export async function listsAllWithItems(fn: StoreFunctions): Promise<ListRecordWithItems> {
	const {
		events: { ListEvent, RecipeEvent },
	} = window.valioApp;

	// Fetch both lists and items in parallel
	const [lists, apiItems] = await Promise.all([getUserLists(), getUserListsWithItems()]);

	// Handle empty state early
	if (lists.length === 0) {
		fn.set({ data: { lists: {} } });
		ListEvent.fetch({ data: {} });
		return {};
	}

	// Extract unique recipe IDs and fetch recipes
	const recipeIds = Array.from(
		new Set(apiItems.map((item) => item.matterId).filter(Boolean))
	) as string[];
	const recipes = await getRecipes({ ids: recipeIds, full: 0, 'include-deleted': 0, limit: -1 });

	// Initialize lists structure
	const listsWithItems = initializeListsRecord(lists);

	// Process each item and add to its list
	for (const apiItem of apiItems) {
		const { listId } = apiItem;
		const itemRecipe = recipes.find((recipe) => recipe.id === Number(apiItem.matterId));

		// Ensure the list exists (in case of data inconsistency)
		if (!listsWithItems[listId]) {
			RecipeEvent.initialDataLoaded({ data: { listId, item: undefined } });
			continue;
		}

		const listItem = transformToListItem(apiItem, itemRecipe);

		listsWithItems[listId].items.push(listItem);
		listsWithItems[listId].itemCount++;

		// Set list thumbnail from first item's image
		if (listItem.order === 0) {
			listsWithItems[listId].image = itemRecipe?.image || undefined;
		}

		// Dispatch event for each item added
		RecipeEvent.initialDataLoaded({ data: { listId, item: listItem } });
	}

	fn.set({ data: { lists: listsWithItems } });
	ListEvent.fetch({ data: listsWithItems });

	return listsWithItems;
}
